import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import InactivityModal from "@/components/InactivityTracker/InactivityTracker.vue";
import Swal from "sweetalert2";
import crudMethods from "./dynamic_forms_setup_crud";
import profileMethods from "./dynamic_forms_setup_profiles";
import validationMethods from "./dynamic_forms_setup_validation";
import { createDynamicFormsState, getHeaders, getJsonHeaders } from "./dynamic_forms_setup_state";

const NEXT_TABS = new Map([
  ["questions", "sections"], ["sections", "forms"], ["forms", "preview"], ["preview", "assignments"],
]);
const PREVIOUS_TABS = new Map([
  ["sections", "questions"], ["forms", "sections"], ["preview", "forms"], ["assignments", "preview"],
]);
const CONTINUE_LABELS = new Map([
  ["questions", "Continue to Sections"], ["sections", "Continue to Forms"],
  ["forms", "Continue to Preview"], ["preview", "Continue to Profile Assignment"],
]);

export default {
  components: {
    HomeNavBar,
    InactivityModal,
  },
  data: createDynamicFormsState,
  mounted() {
    this.load_setup_data();
  },
  methods: {
    ...crudMethods,
    ...profileMethods,
    ...validationMethods,
    get_headers: getHeaders,
    get_json_headers: getJsonHeaders,
    continue_setup() {
      this.select_tab("questions");
    },
    open_tab(tab) {
      this.select_tab(tab.key);
    },
    is_step_complete(tab) {
      const checks = new Map([
        ["questions", () => this.questions.length > 0],
        ["sections", () => this.sections.length > 0],
        ["forms", () => this.forms.length > 0],
        ["preview", () => this.forms.length > 0],
      ]);
      return (checks.get(tab) || (() => this.forms.some(form => form.assigned_profile_types?.length > 0)))();
    },
    can_open_step(tab) {
      const checks = new Map([
        ["questions", () => true],
        ["sections", () => this.questions.length > 0],
        ["forms", () => this.sections.length > 0],
      ]);
      return (checks.get(tab) || (() => this.forms.length > 0))();
    },
    can_continue_step() {
      return this.is_step_complete(this.active_tab);
    },
    continue_to_next_step() {
      if (!this.can_continue_step()) return;
      const next_tab = NEXT_TABS.get(this.active_tab);
      if (next_tab) return this.select_tab(next_tab);
      this.$router.push({ name: "organisation" });
    },
    go_to_previous_step() {
      const previous_tab = PREVIOUS_TABS.get(this.active_tab);
      if (previous_tab) return this.select_tab(previous_tab);
      this.$router.push({ name: "organisation" });
    },
    get_continue_label() {
      return CONTINUE_LABELS.get(this.active_tab) || "Finish Setup";
    },
    select_tab(tab) {
      if (!this.can_open_step(tab)) {
        Swal.fire({ icon: "info", title: "Complete the previous step first", toast: true,
          position: "bottom-end", timer: 1800, showConfirmButton: false });
        return;
      }
      this.active_tab = tab;
      this.show_create_panel = false;
      this.close_assignment();
      this.cancel_edit();
      this.clear_validation();
    },
    create_item() {
      if (!this.validate_current_form()) return;
      if (this.editing_id) {
        this.update_item();
        return;
      }
      if (this.active_tab === "questions") this.create_question();
      if (this.active_tab === "sections") this.create_section();
      if (this.active_tab === "forms") this.create_form();
    },
    post_form(endpoint, form_data, on_success, collection) {
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/${endpoint}/`,
        method: "POST",
        headers: this.get_headers(),
        data: form_data,
        processData: false,
        contentType: false,
        success: async (response) => {
          await Promise.resolve(on_success(response?.data || {}));
          if (collection) await this.fetch_collection(collection);
          this.show_create_panel = false;
          Swal.fire({ icon: "success", title: "Created successfully", toast: true,
            position: "bottom-end", timer: 1800, showConfirmButton: false });
        },
        error: () => Swal.fire({ icon: "error", title: "Unable to create item" }),
      });
    },
    create_question() {
      if (!this.question_form.text || !this.question_form.number || !this.question_form.type) return;
      const data = new FormData();
      data.append("question", this.question_form.text);
      data.append("question_number", this.question_form.number);
      data.append("question_type", this.question_form.type);
      data.append("is_mandatory", this.question_form.mandatory);
      data.append("is_locked", this.question_form.locked);
      if (this.is_multiple_choice()) {
        this.question_form.options.filter(option => option.text.trim()).forEach(option => {
          data.append("options[]", option.text.trim());
        });
      }
      this.post_form("create_question", data, () => {
        this.question_form = { text: "", number: "", type: "", mandatory: false, locked: false, options: [] };
      }, "questions");
    },
    is_multiple_choice() {
      const selected = this.question_types.find(type => type.id === this.question_form.type);
      return selected?.question_type === "multiple_choice";
    },
    handle_question_type_change() {
      this.question_form.options = this.is_multiple_choice() ? [{ text: "" }] : [];
    },
    add_question_option() {
      this.question_form.options.push({ text: "" });
    },
    remove_question_option(index) {
      this.question_form.options.splice(index, 1);
    },
    create_section() {
      if (!this.section_form.name) return;
      const data = new FormData();
      data.append("section_name", this.section_form.name);
      data.append("is_locked", this.section_form.locked);
      this.post_form("create_section", data, async item => {
        if (item.id && this.selected_question_ids.length) {
          await this.assign_questions_to_section(item.id, this.selected_question_ids);
        }
        this.section_form = { name: "", locked: false };
        this.selected_question_ids = [];
      }, "sections");
    },
    create_form() {
      if (!this.dynamic_form.name) return;
      const data = new FormData();
      data.append("form_name", this.dynamic_form.name);
      data.append("description", this.dynamic_form.description);
      this.post_form("create_form", data, async item => {
        this.selected_preview_form_id = item.id;
        if (item.id && this.selected_section_ids.length) {
          await this.assign_sections_to_form(item.id, this.selected_section_ids);
        }
        this.dynamic_form = { name: "", description: "" };
        this.selected_section_ids = [];
      }, "forms");
    },
    load_form_preview() {
      if (!this.selected_preview_form_id) {
        this.preview_sections = [];
        return;
      }
      this.preview_loading = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/view_form_sections/`,
        method: "POST",
        headers: this.get_headers(),
        data: { form_id: this.selected_preview_form_id },
        success: (response) => { this.preview_sections = response?.sections || []; },
        error: () => { this.preview_sections = []; },
        complete: () => { this.preview_loading = false; },
      });
    },
    get_preview_question_type(question = {}) {
      return question?.question_type?.question_type || "text";
    },
    assign_questions_to_section(section_id, question_ids) {
      this.post_assignment("assign_questions", {
        section_id: Number(section_id),
        question_ids: question_ids.map(Number),
      }, "Questions assigned to section");
    },
    assign_sections_to_form(form_id, section_ids) {
      this.post_assignment("assign_form_sections", {
        form_id: Number(form_id),
        section_ids: section_ids.map(Number),
      }, "Sections assigned to form");
    },
    post_assignment(endpoint, payload, success_title) {
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/${endpoint}/`,
        method: "POST",
        headers: this.get_json_headers(),
        data: JSON.stringify(payload),
        success: () => {
          if (endpoint === "assign_form_sections") {
            this.selected_preview_form_id = payload.form_id;
            this.load_form_preview();
          }
          this.close_assignment();
          Swal.fire({ icon: "success", title: success_title, toast: true,
            position: "bottom-end", timer: 1800, showConfirmButton: false });
        },
        error: () => Swal.fire({ icon: "warning", title: "Created, but assignment failed",
          text: "You can retry the assignment from this setup page." }),
      });
    },
    open_question_assignment(section) {
      this.show_create_panel = false;
      this.assignment_type = "questions";
      this.assignment_target = section;
      this.selected_question_ids = [];
      this.load_section_question_assignments(section.id);
    },
    open_question_section_assignment(question) {
      this.show_create_panel = false;
      this.assignment_type = "question_to_section";
      this.assignment_target = question;
      this.assignment_section_id = "";
    },
    open_section_assignment(form) {
      this.show_create_panel = false;
      this.assignment_type = "sections";
      this.assignment_target = form;
      this.selected_section_ids = [];
    },
    load_section_question_assignments(section_id) {
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/filter_questions/`,
        method: "POST",
        headers: this.get_json_headers(),
        data: JSON.stringify({ section_id: Number(section_id) }),
        success: (response) => {
          const section_questions = response?.data?.questions || [];
          this.selected_question_ids = section_questions
            .filter(question => question.is_assigned_to_section)
            .map(question => question.id);
        },
      });
    },
    save_assignment() {
      if (!this.assignment_target?.id) return;
      const handlers = new Map([
        ["question_to_section", () => this.save_question_to_section_assignment()],
        ["questions", () => this.save_questions_assignment()],
        ["sections", () => this.save_sections_assignment()],
      ]);
      handlers.get(this.assignment_type)?.();
    },
    show_assignment_requirement(title) {
      Swal.fire({ icon: "info", title, toast: true,
        position: "bottom-end", timer: 1800, showConfirmButton: false });
    },
    save_question_to_section_assignment() {
      if (!this.assignment_section_id) return this.show_assignment_requirement("Select a section");
      this.assign_questions_to_section(this.assignment_section_id, [this.assignment_target.id]);
    },
    save_questions_assignment() {
      if (!this.selected_question_ids.length) return this.show_assignment_requirement("Select at least one question");
      this.assign_questions_to_section(this.assignment_target.id, this.selected_question_ids);
    },
    save_sections_assignment() {
      if (!this.selected_section_ids.length) return this.show_assignment_requirement("Select at least one section");
      this.assign_sections_to_form(this.assignment_target.id, this.selected_section_ids);
    },
    close_assignment() {
      this.assignment_target = null;
      this.assignment_type = "";
      this.selected_question_ids = [];
      this.selected_section_ids = [];
      this.assignment_section_id = "";
    },
  },
};
